import React, { useState, useCallback, useEffect } from 'react';
import StartScreen from './components/StartScreen';
import RuntimeScreen from './components/RuntimeScreen';
import GlobalProvider from './utils/GlobalContext';
import './App.css';
import MinetestArgs from './utils/MinetestArgs';
import { PROXIES } from './utils/common';

export type GameId = 'minetest_game' | 'mineclonia' | 'mineclone2' | 'glitch' | 'blockbomber';

// Define the game options interface
export interface GameOptions {
  language: string;
  proxy: string;
  storagePolicy: string;
  minetestArgs: MinetestArgs;
  mode: 'local' | 'host' | 'join';
  gameId: GameId;
  playerName?: string;
  password?: string;
  joinCode?: string;
  worldName?: string;
  realmId?: string;
  // Config del mundo del realm ("modo,daño,plano" p.ej. "creative,0,1") — se
  // aplica al world.mt la primera vez que se prende el realm.
  realmCfg?: string;
}

const initial_proxy = PROXIES[parseInt(localStorage.getItem('luanti_wasm_selected_proxy') || '0')];

// Modo multiplayer pedido por el hub al (re)cargar el iframe:
//   ?pcmode=host|join & game=<gameId> & code=<clientCode> & name=<playerName>
// Sin estos params, el juego arranca normal (mode 'local' → menú nativo).
const pc_mp = (() => {
  try {
    const p = new URLSearchParams(window.location.search);
    const mode = p.get('pcmode');
    if (mode === 'host' || mode === 'join') {
      return {
        mode: mode as 'host' | 'join',
        gameId: (p.get('game') || 'mineclone2') as GameId,
        joinCode: p.get('code') || undefined,
        playerName: p.get('name') || undefined,
        worldName: p.get('world') || undefined,
        realmId: p.get('realm') || undefined,
        realmCfg: p.get('rcfg') || undefined,
      };
    }
  } catch { /* ignore */ }
  return null;
})();

// Nombre de usuario de la plataforma (lo manda el hub en ?pcname=, también en
// arranque normal sin pcmode). Se usa como nombre del jugador en singleplayer.
const pc_name = (() => {
  try { return new URLSearchParams(window.location.search).get('pcname') || undefined; }
  catch { return undefined; }
})();

function App() {
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const [zipLoaderPromise, setZipLoaderPromise] = useState<Promise<Uint8Array> | null>(null);
  const [serverExitTimestamp, setServerExitTimestamp] = useState<Date | null>(null);
  const [gameOptions, setGameOptions] = useState<GameOptions>({
    language: 'en',
    proxy: initial_proxy[0],
    storagePolicy: 'indexeddb',
    minetestArgs: new MinetestArgs(),
    mode: pc_mp?.mode || 'local',
    gameId: pc_mp?.gameId || 'minetest_game',
    joinCode: pc_mp?.joinCode,
    playerName: pc_mp?.playerName || pc_name,
    worldName: pc_mp?.worldName,
    realmId: pc_mp?.realmId,
    realmCfg: pc_mp?.realmCfg,
  });

  // Limpiamos el query del modo multiplayer: si el juego se reinicia (ESC →
  // salir → window.location.reload), NO queremos re-disparar host/join.
  useEffect(() => {
    if (pc_mp) {
      try { window.history.replaceState({}, document.title, window.location.pathname); } catch { /* ignore */ }
    }
  }, []);

  const handleStartGame = useCallback((options: GameOptions) => {
    setGameOptions(options);
    setIsGameStarted(true);
    setHasFailed(false);
  }, []);
  
  const updateGameOptions = useCallback((options: Partial<GameOptions>) => {
    setGameOptions(prevOptions => ({
      ...prevOptions,
      ...options
    }));
  }, []);

  const handleExitDetected = useCallback((exitCode: number) => {
    console.log('Game exited with code:', exitCode);
    // Avisar al hub CÓMO terminó la sesión: code 0 = salida voluntaria; != 0 =
    // muerte involuntaria (p.ej. el HOST del realm se fue y el server nos echó).
    // El hub usa esto para la MIGRACIÓN DE HOST: si éramos joiner de un realm y
    // el server murió, re-prende el realm automáticamente (nuevo host).
    try {
      window.parent.postMessage({ source: 'piolacraft-game', type: 'PIOLA_GAME_EXIT', code: exitCode }, '*');
    } catch (e) { /* sin parent */ }
    // Avisar al hub que dejamos de hostear (suelta el lock del realm al instante;
    // sin esto queda 35s de "prendido fantasma" y un amigo que se une recibe un
    // código VPN muerto). OJO AL ORDEN: el aviso va DESPUÉS de la subida final —
    // el PUT del mundo exige ser host; soltarlo antes daba 403 y la partida
    // entera se perdía (bug del 8/7: "no veo las construcciones del host").
    const unhost = () => {
      try {
        window.parent.postMessage({ source: 'piolacraft-game', type: 'PIOLA_UNHOSTING' }, '*');
      } catch (e) { /* sin parent */ }
    };
    // Realm host: subir la última versión del mundo ANTES de recargar (el
    // shutdown del server recién guardó map.sqlite). Tope de 15s por si el
    // backend no responde — el reload no puede quedar colgado.
    const fin = (window as any).__pcRealmFinalUpload as undefined | (() => Promise<void>);
    if (fin) {
      Promise.race([fin().catch(() => { /* best effort */ }), new Promise(r => setTimeout(r, 15000))])
        .then(() => { unhost(); window.location.reload(); });
    } else {
      unhost();
      window.location.reload();
    }
  }, []);

  const handleServerExitIntentDetected = useCallback(() => {
    console.log('Server exit intent detected');
    setServerExitTimestamp(new Date());
  }, []);
  
  const handleGameStatus = useCallback((status: 'running' | 'failed') => {
    console.log('handleGameStatus called with status:', status);
    if (status === 'failed') {
      // Si el juego murió, avisar al hub que ya no hosteamos: sin esto el hub
      // sigue latiendo el lock del realm con el server MUERTO y los amigos que
      // se unen reciben un código VPN que no contesta (timeout de 30s).
      try {
        window.parent.postMessage({ source: 'piolacraft-game', type: 'PIOLA_UNHOSTING' }, '*');
      } catch (e) { /* sin parent */ }
      // If game fails to start, go back to start screen
      setIsGameStarted(false);
      setHasFailed(true);
    }
  }, []);

  return (
    <div className="min-h-screen w-full bg-gray-900 text-white">
      <GlobalProvider
        onExitDetected={handleExitDetected}
        onServerExitIntentDetected={handleServerExitIntentDetected}
      >
        {!isGameStarted ? (
          <StartScreen 
            onStartGame={handleStartGame}
            updateGameOptions={updateGameOptions}
            currentOptions={gameOptions}
            zipLoaderPromise={zipLoaderPromise}
            setZipLoaderPromise={setZipLoaderPromise}
            hasFailed={hasFailed}
          />
        ) : (
          <RuntimeScreen
            gameOptions={gameOptions}
            onGameStatus={handleGameStatus}
            zipLoaderPromise={zipLoaderPromise}
            serverExitTimestamp={serverExitTimestamp}
          />
        )}
      </GlobalProvider>
    </div>
  );
}

export default App; 